import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { useGlobalContext } from "../../data/GlobalContext"
import { promoURL } from "../../../App"

export default function RiseOfHeroesGame({ onClose }) {
  const { username } = useGlobalContext()
  const [showSelection, setShowSelection] = useState(false)
  const [showWin, setShowWin] = useState(false)
  const [flippedCard, setFlippedCard] = useState(null)
  const [isEligible, setIsEligible] = useState(true)
  const [winPrize, setWinPrize] = useState(null)
  const [loadingEligibility, setLoadingEligibility] = useState(true)
  const [showAlert, setShowAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState("")
  const [eligibilityStatus, setEligibilityStatus] = useState(null)
  const [imagesReady, setImagesReady] = useState(false)

  const showAlertMessage = (message) => {
    setAlertMessage(message)
    setShowAlert(true)
  }

  const container = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  }

  const VALID_PRIZES = [50, 150, 300, 400, 500]

  // Card Animation
  const card = {
    hidden: {
      opacity: 0,
      y: 80,
      scale: 0.9,
    },
    show: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  }

  const CARDS = [
    {
      card: "/images/game/RISE_OF_HEROES_CARD(1).webp",
      letter: "/images/game/RISE_OF_LETTER(F).webp",
    },
    {
      card: "/images/game/RISE_OF_HEROES_CARD(2).webp",
      letter: "/images/game/RISE_OF_LETTER(G).webp",
    },
    {
      card: "/images/game/RISE_OF_HEROES_CARD(3).webp",
      letter: "/images/game/RISE_OF_LETTER(H).webp",
    },
  ]

  const [shuffledCards, setShuffledCards] = useState(() => [...CARDS])
  const [cardsShuffled, setCardsShuffled] = useState(false)

  const shuffleCards = (cards) => {
    const shuffled = [...cards]

    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1))
      ;[shuffled[index], shuffled[randomIndex]] = [
        shuffled[randomIndex],
        shuffled[index],
      ]
    }

    // Avoid returning the original order so the hidden cards really change.
    const orderDidNotChange = shuffled.every(
      (item, index) => item.card === cards[index].card
    )

    if (orderDidNotChange && shuffled.length > 1) {
      ;[shuffled[0], shuffled[1]] = [shuffled[1], shuffled[0]]
    }

    return shuffled
  }

  const SELECTION_IMAGES = [
    "/images/game/ROH_TITLE.webp",
    "/images/game/RISE_OF_HEROES_BACK_CARD.webp",
    "/images/game/ROH_CLOSE_BTN.webp",
    ...CARDS.flatMap(({ card, letter }) => [card, letter]),
  ]

  //Checking player's eligibility
  useEffect(() => {
    const checkEligibility = async () => {
      try {
        const response = await fetch(
          `${promoURL}?cmd=riseOfHeroes&u=${username}`
        )

        const data = await response.json()

        if (Object.keys(data).length === 0) {
          setIsEligible(false)
          setEligibilityStatus("notEligible")
          return
        }

        const result = data[username]

        if (!result) {
          setIsEligible(false)
          setEligibilityStatus("notEligible")
          return
        }

        const [claimed, prize, alreadySelected] = result.split("|")

        if (alreadySelected === "true") {
          setIsEligible(false)
          setEligibilityStatus("alreadySelected")
        } else {
          setIsEligible(true)
          setEligibilityStatus(null)
        }
      } catch (error) {
        console.error(error)
        setIsEligible(false)
        setEligibilityStatus("notEligible")
      } finally {
        setLoadingEligibility(false)
      }
    }

    if (username) {
      checkEligibility()
    }
  }, [username])

  const handleFlip = async (index) => {
    if (flippedCard !== null) return

    try {
      const response = await fetch(promoURL, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
        body: new URLSearchParams({
          cmd: "claimRiseOfHeroes",
          u: username,
        }).toString(),
      })

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
      }

      const data = await response.json()

      // Supports values such as "50", 50, or "{50}"
      const prize = Number(String(data?.s ?? "").replace(/\D/g, ""))

      if (!VALID_PRIZES.includes(prize)) {
        showAlertMessage("You are not eligible to participate.")
        return
      }

      setWinPrize(prize)
      setFlippedCard(index)

      setTimeout(() => {
        setShowWin(true)
      }, 800)
    } catch (error) {
      console.error("Rise of Heroes API Error:", error)
      showAlertMessage("Something went wrong. Please try again.")
    }
  }

  useEffect(() => {
    let cancelled = false

    const preloadImage = (src) =>
      new Promise((resolve) => {
        const image = new Image()

        image.onload = async () => {
          try {
            await image.decode()
          } catch {
            // decode() may reject even when the image loaded successfully
          }

          resolve(src)
        }

        image.onerror = () => resolve(src)
        image.src = src
      })

    Promise.all(SELECTION_IMAGES.map(preloadImage)).then(() => {
      if (!cancelled) {
        setImagesReady(true)
      }
    })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!imagesReady) return

    const preloadPrizeBanners = () => {
      VALID_PRIZES.forEach((prize) => {
        const image = new Image()
        image.src = `/images/game/ROH_LANTERN-BANNER-${prize}-FREE_SPINS.webp`
      })
    }

    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(preloadPrizeBanners)

      return () => window.cancelIdleCallback(id)
    }

    const timeout = window.setTimeout(preloadPrizeBanners, 1000)
    return () => window.clearTimeout(timeout)
  }, [imagesReady])

  return (
    <>
      {!showSelection ? (
        <div className="rise-of-heroes rise-of-heroes-first-slide">
          <div className="rise-of-heroes--lantern-left swing">
            <img src="/images/game/ROH_LANTERN-SWINGING.webp" alt="" />
          </div>

          <div className="rise-of-heroes--banner zoom-animation">
            <img
              src="/images/game/ROH_LANTERN-BANNER.webp"
              alt=""
              loading="eager"
              fetchPriority="high"
              decoding="async"
              width={1200}
              height={500}
            />
          </div>

          <div className="rise-of-heroes--dragon">
            <img src="/images/game/ROF_DRAGON.webp" alt="" />
          </div>

          <div className="rise-of-heroes--tiger">
            <img
              src="/images/game/ROH_TIGER.webp"
              alt=""
              loading="lazy"
              decoding="async"
            />
          </div>

          <div className="rise-of-heroes--btn-content">
            <button
              type="button"
              className="rise-of-heroes--btn"
              disabled={loadingEligibility || !imagesReady}
              onClick={() => {
                if (loadingEligibility || !imagesReady) return

                if (!isEligible) {
                  if (eligibilityStatus === "alreadySelected") {
                    showAlertMessage(
                      "You have already selected a card. You cannot participate again."
                    )
                  } else {
                    showAlertMessage("You are not eligible to participate.")
                  }

                  return
                }

                // Shuffle only once for this game session. Closing and reopening
                // the selection screen keeps the same card in every position.
                if (!cardsShuffled) {
                  setShuffledCards(shuffleCards(CARDS))
                  setCardsShuffled(true)
                }

                setShowSelection(true)
              }}
            >
              <img
                src="/images/game/ROH_PLAY_BTN.webp"
                alt="Play Rise of Heroes"
                loading="eager"
                decoding="async"
              />
            </button>
            <button className="rise-of-heroes--home-btn" onClick={onClose}>
              <img src="/images/game/ROH_HOME_BTN.webp" alt="Close" />
            </button>
          </div>

          <div className="rise-of-heroes--lantern swing">
            <img src="/images/game/ROH_LANTERN-SWINGING.webp" alt="" />
          </div>
        </div>
      ) : (
        <div
          className="rise-of-heroes rise-of-heroes--cards"
          style={{ overflowY: "auto", padding: "0px" }}
        >
          <div className="rise-of-heroes--title">
            <img src="/images/game/ROH_TITLE.webp" alt="" />
          </div>
          <motion.div
            className="rise-of-heroes--characters"
            variants={container}
            initial="hidden"
            animate="show"
          >
            {shuffledCards.map((item, index) => (
              <motion.div
                key={index}
                className={`rise-of-heroes--character-content ${
                  flippedCard === index
                    ? "is-selected"
                    : flippedCard !== null
                      ? "is-dimmed"
                      : ""
                }`}
                variants={card}
                whileHover={
                  flippedCard === null
                    ? {
                        y: -15,
                        scale: 1.08,
                        rotate: index % 2 === 0 ? -3 : 3,
                      }
                    : {}
                }
                whileTap={flippedCard === null ? { scale: 0.95 } : {}}
                animate={
                  flippedCard === null
                    ? {
                        y: [0, -8, 0],
                        scale: 1,
                        opacity: 1,
                        rotate: 0,
                        filter: "brightness(1) saturate(1)",
                      }
                    : flippedCard === index
                      ? {
                          y: -24,
                          scale: 1,
                          opacity: 1,
                          rotate: 0,
                          filter: [
                            "brightness(1) saturate(1)",
                            "brightness(1.35) saturate(1.2)",
                            "brightness(1) saturate(1)",
                          ],
                        }
                      : {
                          y: 12,
                          scale: 0.86,
                          opacity: 0.35,
                          rotate: 0,
                          filter: "brightness(0.55) saturate(0.45)",
                        }
                }
                transition={
                  flippedCard === null
                    ? {
                        y: {
                          repeat: Infinity,
                          duration: 2 + index * 0.3,
                          ease: "easeInOut",
                        },
                        scale: { duration: 0.35 },
                        opacity: { duration: 0.35 },
                      }
                    : flippedCard === index
                      ? {
                          y: {
                            type: "spring",
                            stiffness: 260,
                            damping: 16,
                          },
                          scale: {
                            type: "spring",
                            stiffness: 260,
                            damping: 16,
                          },
                          filter: {
                            repeat: Infinity,
                            duration: 1.2,
                            ease: "easeInOut",
                          },
                        }
                      : {
                          duration: 0.45,
                          ease: [0.22, 1, 0.36, 1],
                        }
                }
                style={{
                  zIndex: flippedCard === index ? 10 : 1,
                }}
              >
                <motion.div
                  className="flip-card"
                  onClick={() => handleFlip(index)}
                  animate={{
                    rotateY: flippedCard === index ? 180 : 0,
                  }}
                  transition={{
                    duration: 0.8,
                    ease: "easeInOut",
                  }}
                  style={{
                    pointerEvents:
                      flippedCard !== null || !isEligible || loadingEligibility
                        ? "none"
                        : "auto",
                    opacity: !isEligible || loadingEligibility ? 0.5 : 1,
                  }}
                >
                  <div className="flip-card-face flip-card-front">
                    <img
                      src="/images/game/RISE_OF_HEROES_BACK_CARD.webp"
                      alt="Select this card"
                    />
                  </div>

                  <div className="flip-card-face flip-card-back">
                    <img src={item.card} alt="Selected hero card" />
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>

          <button
            type="button"
            className="rise-of-heroes--close-btn"
            onClick={() => {
              setShowSelection(false)
            }}
          >
            <img src="/images/game/ROH_CLOSE_BTN.webp" alt="Back" />
          </button>
        </div>
      )}

      {showWin && winPrize !== null && (
        <div className="rise-of-heroes-win">
          <img
            className="rise-of-heroes-win--banner"
            src={`/images/game/ROH_LANTERN-BANNER-${winPrize}-FREE_SPINS.webp`}
            alt={`${winPrize} Free Spins`}
          />

          <button
            className="rise-of-heroes-win--close-btn"
            type="button"
            onClick={() => setShowWin(false)}
          >
            <img src="/images/game/ROH_CLOSE_BTN.webp" alt="Back" />
          </button>
        </div>
      )}

      {showAlert && (
        <div className="rise-of-heroes-alert-overlay">
          <div className="rise-of-heroes-alert-modal">
            <p>{alertMessage}</p>

            <button type="button" onClick={() => setShowAlert(false)}>
              OK
            </button>
          </div>
        </div>
      )}
    </>
  )
}
